import api, { ApiResponse, PageResponse } from './api'
import type { Payout, BankAccount, PersonalPayoutAccount, PayoutCapability, PayoutFeeConfig } from '../types'

interface PayoutListParams {
  page?: number
  size?: number
}

export interface PayoutEligibilityResponse {
  eligible: boolean
  payoutsPaused: boolean
  twoFactorEnabled: boolean
  availableBalance: string
  minPayoutAmount: string
  minPayoutAmounts: Record<PayoutCapability, string>
  enabledCapabilities: PayoutCapability[]
  feeConfigs: Record<PayoutCapability, PayoutFeeConfig>
  usdtOrdinaryExpectedProcessingAt: number
  usdtUrgentExpectedProcessingAt: number
  personalBankExpectedProcessingAt: number
  kycApproved: boolean
  personalKycApproved: boolean
  businessKybApproved: boolean
  reasons: string[]
}

export interface PayoutEwallet {
  chain: 'TRON' | 'BSC' | 'ETH'
  address: string
  createdAt: number
  updatedAt: number
}

export type CreatePayoutMethod = 'BANK_BUSINESS' | 'USDT' | 'PERSONAL_EWALLET' | 'PERSONAL_BANK'

interface CreatePayoutRequest {
  amount: string
  method: CreatePayoutMethod
  bankAccountId?: string
  personalPayoutAccountId?: string
  usdtServiceType?: 'ORDINARY' | 'URGENT'
  totpCode?: string
}

interface AddBankAccountRequest {
  accountHolderName: string
  bankName: string
  bankCode: string
  branchCode?: string
  accountNumber: string
  region: string
  fedWire?: string
  bankAddress?: string
  isDefault?: boolean
}

interface AddPersonalPayoutAccountRequest {
  accountKind: 'BANK' | 'E_WALLET'
  paymentMethod: string
  country: string
  currency: string
  accountHolderName?: string
  firstName?: string
  lastName?: string
  mobile?: string
  bankName?: string
  bankCode?: string
  accountNumber?: string
  certNumber?: string
  clearingNetwork?: string
  fields?: { key: string; value: string; displayValue?: string }[]
}

export function checkPayoutEligibility(): Promise<ApiResponse<PayoutEligibilityResponse>> {
  return api.get('/v1/merchant/payouts/eligibility')
}

export function createPayout(data: CreatePayoutRequest): Promise<ApiResponse<Payout>> {
  return api.post('/v1/merchant/payouts', data)
}

export function getPayouts(params: PayoutListParams): Promise<ApiResponse<PageResponse<Payout>>> {
  return api.get('/v1/merchant/payouts', { params })
}

export function getPayout(id: string): Promise<ApiResponse<Payout>> {
  return api.get(`/v1/merchant/payouts/${id}`)
}

export function getBankAccounts(): Promise<ApiResponse<BankAccount[]>> {
  return api.get('/v1/merchant/bank-accounts')
}

export function getPayoutEwallet(): Promise<ApiResponse<PayoutEwallet | null>> {
  return api.get('/v1/merchant/payout-ewallet')
}

export function createPayoutEwallet(data: {
  chain: PayoutEwallet['chain']
  address: string
  totpCode: string
}): Promise<ApiResponse<PayoutEwallet>> {
  return api.post('/v1/merchant/payout-ewallet', data)
}

export function deletePayoutEwallet(data: { totpCode: string }): Promise<ApiResponse<null>> {
  return api.delete('/v1/merchant/payout-ewallet', { data })
}

export function addBankAccount(data: AddBankAccountRequest): Promise<ApiResponse<BankAccount>> {
  return api.post('/v1/merchant/bank-accounts', data)
}

export function deleteBankAccount(id: string): Promise<ApiResponse<null>> {
  return api.delete(`/v1/merchant/bank-accounts/${id}`)
}

export function getPersonalPayoutAccounts(): Promise<ApiResponse<PersonalPayoutAccount[]>> {
  return api.get('/v1/merchant/personal-payout-accounts')
}

export function addPersonalPayoutAccount(data: AddPersonalPayoutAccountRequest): Promise<ApiResponse<PersonalPayoutAccount>> {
  return api.post('/v1/merchant/personal-payout-accounts', data)
}

export function deletePersonalPayoutAccount(id: string): Promise<ApiResponse<null>> {
  return api.delete(`/v1/merchant/personal-payout-accounts/${id}`)
}
